import { Badge, Group, NavLink, ScrollArea, Stack, Text, Tooltip } from '@mantine/core';
import { IconAbc, IconBraces, IconFileText, IconRepeat, IconUsers } from '@tabler/icons-react';

import type { BindingCatalog, CatalogCollection, CatalogValue } from '@/types';

/** What the user has armed in the panel, awaiting a document selection. */
export type ArmedItem =
  | {
      type: 'value';
      path: string;
      label: string;
      valueType: string;
      /** ENUM only: the closed vocabulary, so the pending card can offer a
       * "Colour by value" (appearance_by) control on a status cell. */
      allowedValues?: string[] | null;
    }
  | { type: 'collection'; path: string; label: string }
  | { type: 'component'; slug: string; name: string; isData: boolean }
  | { type: 'subjectLoop' }
  | { type: 'gate' }
  | { type: 'emptyState' };

export interface CatalogPanelProps {
  catalog: BindingCatalog;
  armed: ArmedItem | null;
  onArm: (item: ArmedItem | null) => void;
  /** Collection paths of the loops enclosing the current document selection
   * (innermost last) — their record keys are promoted/highlighted. */
  activeLoopPaths?: string[];
  /** Inline report_metadata key creation (delegates to the page). */
  onCreateMetadataKey?: () => void;
}

/** Arm from a nested NavLink without toggling every ancestor NavLink —
 * Mantine bubbles clicks, so each handler must stop propagation. */
function armedKey(item: ArmedItem | null): string | null {
  if (!item) {
    return null;
  }
  switch (item.type) {
    case 'value':
      return `value:${item.path}`;
    case 'collection':
      return `collection:${item.path}`;
    case 'component':
      return `component:${item.slug}`;
    default:
      return item.type;
  }
}

/** The always-visible catalog: every component, collection, record field,
 * subject field, and report_metadata key the fill service can supply.
 * Clicking arms an item; the document selection then completes the gesture. */
export function CatalogPanel({
  catalog,
  armed,
  onArm,
  activeLoopPaths = [],
  onCreateMetadataKey,
}: CatalogPanelProps) {
  const currentKey = armedKey(armed);

  const toggle = (item: ArmedItem) => (event: React.MouseEvent) => {
    event.stopPropagation();
    onArm(armedKey(item) === currentKey ? null : item);
  };

  const valueLink = (value: CatalogValue, indent: number) => (
    <NavLink
      key={`v:${value.path}:${indent}`}
      label={
        <Group gap={6} wrap="nowrap">
          <IconAbc size={13} style={{ flexShrink: 0 }} />
          <Text size="xs" truncate>
            {value.label}
          </Text>
          {value.derived && (
            <Badge size="xs" variant="light">
              derived
            </Badge>
          )}
        </Group>
      }
      description={value.path}
      active={currentKey === `value:${value.path}`}
      onClick={toggle({
        type: 'value',
        path: value.path,
        label: value.label,
        valueType: value.type,
        allowedValues: value.allowed_values,
      })}
      pl={12 + indent * 12}
      styles={{ label: { minWidth: 0 } }}
    />
  );

  const collectionLinks = (entry: CatalogCollection, indent: number): React.ReactNode => (
    <div key={`c:${entry.path}:${indent}`}>
      <NavLink
        label={
          <Group gap={6} wrap="nowrap">
            <IconRepeat size={13} style={{ flexShrink: 0 }} />
            <Text size="xs" fw={600} truncate>
              {entry.label}
            </Text>
            {activeLoopPaths.includes(entry.path) && (
              <Badge size="xs" color="grape" variant="light">
                in loop
              </Badge>
            )}
          </Group>
        }
        description={entry.path}
        active={currentKey === `collection:${entry.path}`}
        onClick={toggle({ type: 'collection', path: entry.path, label: entry.label })}
        pl={12 + indent * 12}
        defaultOpened={activeLoopPaths.includes(entry.path)}
        childrenOffset={0}
      >
        {entry.values.map((value) => valueLink(value, indent + 1))}
        {(entry.collections ?? []).map((nested) => collectionLinks(nested, indent + 1))}
      </NavLink>
    </div>
  );

  const dataSlugs = new Set(Object.keys(catalog.schema_versions));
  const componentEntries: Array<{ slug: string; name: string; isData: boolean }> = [
    ...catalog.narrative_sections.map((s) => ({ slug: s.slug, name: s.name, isData: false })),
    ...[...dataSlugs]
      .filter((slug) => !catalog.narrative_sections.some((s) => s.slug === slug))
      .map((slug) => ({ slug, name: slug.replaceAll('-', ' '), isData: true })),
  ];

  return (
    <ScrollArea h="100%" type="auto">
      <Stack gap={2} p="xs">
        <Text size="xs" fw={700} c="dimmed" tt="uppercase">
          Structure
        </Text>
        <NavLink
          label={
            <Group gap={6} wrap="nowrap">
              <IconUsers size={13} />
              <Text size="xs" fw={600}>
                Subject loop
              </Text>
            </Group>
          }
          description="Repeats a region per subject (optional — without it everything fills from the first subject)"
          active={currentKey === 'subjectLoop'}
          onClick={toggle({ type: 'subjectLoop' })}
        />
        <NavLink
          label={
            <Text size="xs" fw={600}>
              Subject-type gate
            </Text>
          }
          description="Keep a region only for company or individual subjects"
          active={currentKey === 'gate'}
          onClick={toggle({ type: 'gate' })}
        />
        <NavLink
          label={
            <Text size="xs" fw={600}>
              Empty state
            </Text>
          }
          description="Attach an authored block to a loop, shown when it has no items"
          active={currentKey === 'emptyState'}
          onClick={toggle({ type: 'emptyState' })}
        />

        <Text size="xs" fw={700} c="dimmed" tt="uppercase" mt="sm">
          Components
        </Text>
        {componentEntries.map((component) => (
          <NavLink
            key={component.slug}
            label={
              <Group gap={6} wrap="nowrap">
                {component.isData ? <IconBraces size={13} /> : <IconFileText size={13} />}
                <Text size="xs" fw={600} truncate>
                  {component.name}
                </Text>
                <Tooltip
                  label={
                    component.isData
                      ? 'Structured output — bind its values and loops'
                      : 'Narrative prose — bind as a section'
                  }
                >
                  <Badge size="xs" variant="light" color={component.isData ? 'grape' : 'teal'}>
                    {component.isData ? 'data' : 'prose'}
                  </Badge>
                </Tooltip>
              </Group>
            }
            active={currentKey === `component:${component.slug}`}
            onClick={toggle({
              type: 'component',
              slug: component.slug,
              name: component.name,
              isData: component.isData,
            })}
            childrenOffset={0}
            defaultOpened={false}
          >
            {catalog.subjects.values
              .filter((value) => value.component_slug === component.slug)
              .map((value) => valueLink(value, 1))}
            {(catalog.subjects.collections ?? [])
              .filter((entry) => entry.component_slug === component.slug)
              .map((entry) => collectionLinks(entry, 1))}
          </NavLink>
        ))}

        <Text size="xs" fw={700} c="dimmed" tt="uppercase" mt="sm">
          Subject fields
        </Text>
        {catalog.subjects.values
          .filter((value) => !value.component_slug)
          .map((value) => valueLink(value, 0))}

        <Group justify="space-between" mt="sm">
          <Text size="xs" fw={700} c="dimmed" tt="uppercase">
            Report fields
          </Text>
          {onCreateMetadataKey && (
            <Text size="xs" c="blue" style={{ cursor: 'pointer' }} onClick={onCreateMetadataKey}>
              + add key
            </Text>
          )}
        </Group>
        {catalog.report_values.map((value) => valueLink(value, 0))}
      </Stack>
    </ScrollArea>
  );
}
